import { Box, CircularProgress } from "@mui/material";

/**
 * TlButton — Teleredo design-system button
 *
 * variant  — "primary" | "secondary" | "ghost" | "soft" | "danger" | "icon"
 * size     — "sm" (34px) | "md" (40px) | "lg" (48px)
 * pill     — true forces --r-pill radius (hero CTAs, Sign In)
 * loading  — shows spinner, keeps width stable
 * softColor — CSS token name for soft variant bg/text (e.g. "--c-violet")
 * fullWidth — stretches to container
 */

const VARIANT_SX = {
  primary: {
    background: "var(--grad-brand-135)",
    color: "#fff",
    border: "none",
    boxShadow: "var(--shadow-cta)",
    "&:hover:not(:disabled)": { filter: "brightness(1.06)" },
    "&:disabled": { background: "var(--grad-brand-135)", color: "rgba(255,255,255,.5)" },
  },
  secondary: {
    background: "var(--surface-card)",
    color: "var(--ink-800)",
    border: "1px solid var(--line-200)",
    boxShadow: "var(--shadow-xs)",
    "&:hover:not(:disabled)": { background: "var(--surface-2)" },
  },
  ghost: {
    background: "transparent",
    color: "var(--ink-600)",
    border: "none",
    boxShadow: "none",
    "&:hover:not(:disabled)": { background: "var(--surface-2)", color: "var(--ink-800)" },
  },
  soft: {
    border: "none",
    boxShadow: "none",
    "&:hover:not(:disabled)": { opacity: 0.84 },
  },
  danger: {
    background: "var(--danger)",
    color: "#fff",
    border: "none",
    boxShadow: "none",
    "&:hover:not(:disabled)": { filter: "brightness(1.08)" },
  },
  icon: {
    background: "transparent",
    color: "var(--ink-500)",
    border: "none",
    boxShadow: "none",
    padding: "0 !important",
    borderRadius: "var(--r-md) !important",
    "&:hover:not(:disabled)": { background: "var(--surface-2)", color: "var(--ink-800)" },
  },
};

const SIZE = {
  sm: { height: 34, paddingInline: "16px", fontSize: "var(--t-body-sm)", iconFontSize: 14, gap: "6px" },
  md: { height: 40, paddingInline: "20px", fontSize: "var(--t-body)",    iconFontSize: 16, gap: "7px" },
  lg: { height: 48, paddingInline: "24px", fontSize: "var(--t-body)",    iconFontSize: 18, gap: "8px" },
};

const ICON_SIZE = {
  sm: { width: 30, height: 30, minWidth: 30 },
  md: { width: 36, height: 36, minWidth: 36 },
  lg: { width: 44, height: 44, minWidth: 44 },
};

const SPINNER_SIZE = { sm: 14, md: 16, lg: 18 };

export default function TlButton({
  variant = "primary",
  size = "md",
  pill = false,
  loading = false,
  startIcon,
  endIcon,
  children,
  disabled,
  fullWidth = false,
  softColor = "--c-violet",
  onClick,
  type = "button",
  "aria-label": ariaLabel,
  sx,
  ...props
}) {
  const isIconOnly = variant === "icon";
  const sizeSx     = isIconOnly ? ICON_SIZE[size] : SIZE[size];
  const variantSx  = VARIANT_SX[variant];

  const softSx = variant === "soft"
    ? { background: `var(${softColor}-bg)`, color: `var(${softColor})` }
    : {};

  return (
    <Box
      component="button"
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      sx={{
        // Reset
        appearance: "none",
        cursor: "pointer",
        userSelect: "none",
        // Layout
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: SIZE[size].gap,
        // Sizing
        height: sizeSx.height,
        ...(isIconOnly
          ? { width: sizeSx.width, minWidth: sizeSx.minWidth, paddingInline: 0 }
          : { paddingInline: sizeSx.paddingInline }),
        width: fullWidth ? "100%" : undefined,
        // Shape
        borderRadius: pill ? "var(--r-pill)" : "var(--r-md)",
        // Type
        fontFamily: "var(--font-ui)",
        fontWeight: 600,
        fontSize: SIZE[size].fontSize,
        lineHeight: 1,
        whiteSpace: "nowrap",
        // Motion (respects prefers-reduced-motion via tokens.css guard)
        transition: "box-shadow .15s, background .15s, transform .1s, filter .15s, opacity .15s",
        "&:active:not(:disabled)": { transform: "translateY(1px)" },
        "&:disabled": { opacity: 0.4, cursor: "not-allowed", boxShadow: "none", transform: "none" },
        "&:focus-visible": {
          outline: "none",
          boxShadow: "0 0 0 2px var(--surface-card), 0 0 0 4px rgba(59,125,240,.45)",
        },
        // Variant
        ...variantSx,
        ...softSx,
        // Caller overrides
        ...sx,
      }}
      {...props}
    >
      {loading ? (
        <CircularProgress
          size={SPINNER_SIZE[size]}
          sx={{ color: "inherit", flexShrink: 0 }}
        />
      ) : (
        <>
          {startIcon && (
            <Box
              component="span"
              aria-hidden="true"
              sx={{ display: "flex", alignItems: "center", "& svg": { fontSize: SIZE[size].iconFontSize } }}
            >
              {startIcon}
            </Box>
          )}
          {children}
          {endIcon && (
            <Box
              component="span"
              aria-hidden="true"
              sx={{ display: "flex", alignItems: "center", "& svg": { fontSize: SIZE[size].iconFontSize } }}
            >
              {endIcon}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
